import { DateTime, type DateTimeFormatOptions } from 'luxon';
import type { SQLDate, SQLDateArray } from '@/modules/common/types/sql-date.type.ts';
import { SentryUtil } from '@/modules/common/utils/sentry.util.ts';
import { toValue } from 'vue';
import type { OverrideFn } from '@/modules/common/types/luxon.type.ts';

type DateInput = DateTime | number | SQLDate;

const DATE_FORMAT_KEYS: readonly (keyof DateTimeFormatOptions)[] = [
  'weekday',
  'era',
  'year',
  'month',
  'day',
  'dateStyle',
];
const TIME_FORMAT_KEYS: readonly (keyof DateTimeFormatOptions)[] = [
  'hour',
  'minute',
  'second',
  'fractionalSecondDigits',
  'dayPeriod',
  'timeZoneName',
  'timeStyle',
  'hour12',
  'hourCycle',
];

export class LuxonUtil {
  private static toDateTime(value: DateInput): DateTime {
    if (typeof value === 'number') {
      return DateTime.now().plus({ days: value });
    }

    if (typeof value === 'string') {
      return DateTime.fromSQL(value);
    }

    return value;
  }

  static getSQLDateArray(start: DateInput, end: DateInput = start): SQLDateArray {
    const sqlStart = LuxonUtil.toDateTime(start).startOf('day').toSQL({ includeOffset: false });
    const sqlEnd = LuxonUtil.toDateTime(end).endOf('day').toSQL({ includeOffset: false });

    if (!sqlStart || !sqlEnd) {
      throw new Error('Invalid date range');
    }

    return [sqlStart, sqlEnd];
  }

  static formatSQL(
    value: SQLDate | null,
    locale: string,
    format: DateTimeFormatOptions = DateTime.DATE_SHORT,
    overrideFn?: OverrideFn
  ) {
    if (!value) {
      return null;
    }

    const dt = DateTime.fromSQL(value);

    if (!dt.isValid) {
      SentryUtil.fallback('Date is invalid');
      return null;
    }

    const resolvedFormat = LuxonUtil.resolveFormat(dt, toValue(format));

    if (!resolvedFormat) {
      return null;
    }

    return overrideFn?.(dt, locale, resolvedFormat) ?? dt.setLocale(locale).toLocaleString(resolvedFormat);
  }

  private static resolveFormat(dt: DateTime<true>, format: DateTimeFormatOptions): DateTimeFormatOptions | null {
    const hasTime = TIME_FORMAT_KEYS.some((key) => key in format);

    if (!hasTime || dt.hour !== 0 || dt.minute !== 0) {
      return format;
    }

    const dateFormat = Object.fromEntries(
      Object.entries(format).filter(([key]) => !TIME_FORMAT_KEYS.includes(key as keyof DateTimeFormatOptions))
    ) as DateTimeFormatOptions;

    return DATE_FORMAT_KEYS.some((key) => key in dateFormat) ? dateFormat : null;
  }
}
